// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { getUserStats, getMyFullStats } from '../services/api';
import { useI18n } from '../i18n';
import SkeletonRows from '../components/SkeletonRows';
import SortableFilterableTable from '../components/SortableFilterableTable';
import ConsommationsClient from '../components/ConsommationsClient';

const selectStyle = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 };

const StatCard = ({ label, value, sub, color, tooltip, valueSize = 24 }) => (
  <div style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, margin: 0 }}>{label}</p>
      {tooltip && (
        <span
          title={tooltip}
          style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--gray)', borderRadius: '50%', width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'help', flexShrink: 0, fontWeight: 700 }}
        >?</span>
      )}
    </div>
    <p style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: valueSize, fontWeight: 800, color: color || 'var(--black)', lineHeight: 1.15,
      fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', margin: 0,
      overflowWrap: 'break-word', wordBreak: 'break-word',
    }}>{value}</p>
    {sub && <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</p>}
  </div>
);

const MesStatistiques = () => {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeRapport, setTypeRapport] = useState('');
  const [ruleFilter, setRuleFilter] = useState('');
  // Période du bloc Consommations. Défaut : le mois en cours — c'est la question
  // que se pose un utilisateur qui ouvre la page (« où j'en suis ce mois-ci ? »).
  const [periode, setPeriode] = useState(() => {
    const maintenant = new Date();
    return { granularite: 'mois', annee: maintenant.getFullYear(), mois: maintenant.getMonth() + 1 };
  });

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [s, f] = await Promise.all([
        getUserStats(),
        getMyFullStats({ typeRapport: typeRapport || undefined, ...periode }),
      ]);
      setStats(s);
      setFull(f);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [typeRapport, periode]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR') : '—';

  const resultatsRows = useMemo(() => {
    const rows = full?.resultats?.par_regle || [];
    return ruleFilter ? rows.filter(r => String(r.rule_number) === ruleFilter) : rows;
  }, [full, ruleFilter]);

  const typeRapportLabel = (tr) => t(`type_rapport_${tr}`) || tr;

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <main className="container mx-auto px-4 py-8 max-w-6xl flex-1">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 8 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--black)' }}>📊 {t('my_stats_title')}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select value={typeRapport} onChange={e => setTypeRapport(e.target.value)} style={selectStyle}>
              <option value="">{t('stat_all_type_rapports')}</option>
              {(full?.resultats?.available_type_rapports || []).map(tr => (
                <option key={tr} value={tr}>{typeRapportLabel(tr)}</option>
              ))}
            </select>
            <Button onClick={load} variant="outline" size="sm">{t('btn_refresh')}</Button>
          </div>
        </div>

        {loading ? (
          <div style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody><SkeletonRows cols={6} rows={5} /></tbody>
            </table>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#ef4444' }}>❌ {error}</div>
        ) : stats && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 28 }}>
              <StatCard label={t('stat_total')}     value={stats.total_analyses}     color="var(--blue)" />
              <StatCard label={t('stat_completed')} value={stats.completed_analyses} color="#22c55e" sub={`${stats.success_rate}% ${t('stat_success_pct')}`} />
              <StatCard label={t('stat_error')}     value={stats.error_analyses}     color="#ef4444" />
              <StatCard label={t('stat_avg_score')} value={stats.avg_score != null ? `${stats.avg_score}/100` : '-'} color="#7c3aed" tooltip={t('stat_avg_score_tooltip')} />
              <StatCard label={t('my_stats_first_analysis')} value={formatDate(stats.first_analysis)} valueSize={16} />
              <StatCard label={t('my_stats_last_analysis')}  value={formatDate(stats.last_analysis)}  valueSize={16} />
            </div>

            <ConsommationsClient
              data={full?.consommations}
              granularite={periode.granularite}
              annee={periode.annee}
              mois={periode.mois}
              onChange={(modif) => setPeriode(p => ({ ...p, ...modif }))}
            />

            <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px', marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{t('stat_resultats_title')}</h2>
                <select value={ruleFilter} onChange={e => setRuleFilter(e.target.value)} style={selectStyle}>
                  <option value="">{t('stat_all_rules')}</option>
                  {(full?.resultats?.par_regle || []).map(r => (
                    <option key={r.rule_number} value={r.rule_number}>{`R${r.rule_number} — ${r.rule_name}`}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                <div>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_nb_rapports')}</p>
                  <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{full?.resultats?.nb_rapports ?? 0}</p>
                </div>
                <div>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_favorable')}</p>
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#22c55e', margin: 0 }}>{full?.resultats?.avg_favorable ?? '—'}</p>
                </div>
                <div>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_reserve')}</p>
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b', margin: 0 }}>{full?.resultats?.avg_reserve ?? '—'}</p>
                </div>
                <div>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_defavorable')}</p>
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#ef4444', margin: 0 }}>{full?.resultats?.avg_defavorable ?? '—'}</p>
                </div>
              </div>
              <SortableFilterableTable
                hideSearch
                columns={[
                  { key: 'rule_number', label: t('stat_col_rule_number'), align: 'right' },
                  { key: 'rule_name', label: t('stat_col_rule_name') },
                  { key: 'total', label: t('stat_col_total_avis'), align: 'right' },
                  { key: 'taux_favorable', label: t('stat_col_pct_favorable'), align: 'right', render: r => r.taux_favorable != null ? `${r.taux_favorable}%` : '—' },
                  { key: 'taux_reserve', label: t('stat_col_pct_reserve'), align: 'right', render: r => r.taux_reserve != null ? `${r.taux_reserve}%` : '—' },
                  { key: 'taux_defavorable', label: t('stat_col_pct_defavorable'), align: 'right', render: r => r.taux_defavorable != null ? `${r.taux_defavorable}%` : '—' },
                ]}
                rows={resultatsRows}
                emptyLabel={t('stat_resultats_empty')}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default MesStatistiques;
